import { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles,
  RefreshCw,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Target,
  Zap,
  PiggyBank,
  CreditCard,
  TrendingDown,
  ArrowRight,
  MessageCircle,
  Send,
  Bot,
  User,
  Trash2,
  LayoutList,
} from 'lucide-react';
import { useRecommendations, useAdvisorChat } from '@hooks/useRecommendations';
import PageTutorial from '@components/common/PageTutorial';
import Button from '@components/common/Button';
import EmptyState from '@components/common/EmptyState';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';

const PRIORITY_CONFIG = {
  high: {
    label: 'Alta',
    icon: '🔴',
    className: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    accentClass: 'border-l-red-500',
  },
  medium: {
    label: 'Media',
    icon: '🟡',
    className: 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800',
    badgeClass: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    accentClass: 'border-l-yellow-500',
  },
  low: {
    label: 'Baixa',
    icon: '🟢',
    className: 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800',
    badgeClass: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    accentClass: 'border-l-emerald-500',
  },
};

const TYPE_ICON_MAP = {
  budget: <Target size={20} className="text-primary-600" />,
  savings: <PiggyBank size={20} className="text-emerald-600" />,
  debt: <CreditCard size={20} className="text-red-600" />,
  investment: <TrendingUp size={20} className="text-blue-600" />,
  expense: <TrendingDown size={20} className="text-orange-600" />,
  alert: <AlertTriangle size={20} className="text-amber-600" />,
  achievement: <CheckCircle2 size={20} className="text-emerald-600" />,
  tip: <Zap size={20} className="text-purple-600" />,
};

const CATEGORY_LABELS = {
  budget: 'Orcamento',
  savings: 'Poupanca',
  debt: 'Dividas',
  investment: 'Investimento',
  expense: 'Despesas',
  alert: 'Alerta',
  achievement: 'Conquista',
  tip: 'Dica',
};

const SUGGESTED_QUESTIONS = [
  'Como posso reduzir meus gastos este mes?',
  'Qual a melhor estrategia para quitar minhas dividas?',
  'Quanto devo guardar por mes para minha meta?',
  'Como esta minha saude financeira geral?',
  'Onde posso investir com seguranca?',
  'Crie um plano financeiro para os proximos 3 meses',
];

// ── Recommendation Card ──────────────────────────────────────────────────────
const RecommendationCard = ({ rec }) => {
  const priority = rec.priority || 'low';
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.low;
  const typeIcon = TYPE_ICON_MAP[rec.type] || TYPE_ICON_MAP.tip;
  const categoryLabel = CATEGORY_LABELS[rec.type] || rec.type || 'Geral';

  return (
    <div className={`rounded-2xl border border-l-4 shadow-sm overflow-hidden ${config.className} ${config.accentClass}`}>
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-white dark:bg-gray-800 flex items-center justify-center flex-shrink-0 shadow-sm">
            {typeIcon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.badgeClass}`}>
                {config.icon} Prioridade {config.label}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-600">
                {categoryLabel}
              </span>
              {rec.source === 'ai' && (
                <span className="text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-0.5 rounded-full border border-purple-200 dark:border-purple-700">
                  <Sparkles size={10} className="inline mr-0.5" />
                  IA
                </span>
              )}
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white leading-snug">
              {rec.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
              {rec.message || rec.description}
            </p>
            {rec.amount && (
              <p className="text-sm font-medium text-primary-600 dark:text-primary-400 mt-1">
                Impacto estimado: {formatCurrency(rec.amount)}
              </p>
            )}
          </div>
        </div>
        {rec.action && (
          <div className="mt-3 ml-14">
            <p className="text-xs text-primary-600 dark:text-primary-400 font-medium">
              💡 {rec.action}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const RecommendationSkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 animate-pulse">
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
      <div className="flex-1">
        <div className="flex gap-2 mb-2">
          <div className="h-5 w-28 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
        </div>
        <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
        <div className="h-3 w-full bg-gray-200 dark:bg-gray-700 rounded mb-1" />
        <div className="h-3 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
  </div>
);

// Tela de carregamento inicial enquanto as regras ainda não chegaram
const AdvisorLoadingScreen = () => (
  <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
    {/* Ícone animado */}
    <div className="relative w-20 h-20">
      <div className="absolute inset-0 rounded-full bg-purple-500/20 animate-ping" />
      <div className="relative w-20 h-20 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
        <Sparkles size={32} className="text-purple-500 animate-pulse" />
      </div>
    </div>

    {/* Texto principal */}
    <div>
      <p className="text-lg font-semibold text-gray-900 dark:text-white">
        O Consultor está avaliando suas finanças
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Analisando transações, metas e padrões de gastos...
      </p>
    </div>

    {/* Barra de progresso indeterminada */}
    <div className="w-64 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full animate-advisorProgress" />
    </div>

    {/* Itens animados sequencialmente */}
    <div className="space-y-2 w-full max-w-sm">
      {[
        { label: 'Lendo suas transações', delay: '0ms' },
        { label: 'Verificando orçamentos', delay: '400ms' },
        { label: 'Calculando padrões', delay: '800ms' },
      ].map(({ label, delay }) => (
        <div
          key={label}
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 animate-pulse"
          style={{ animationDelay: delay }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" />
          {label}
        </div>
      ))}
    </div>
  </div>
);

// Banner exibido enquanto a IA ainda está calculando (regras já visíveis)
const AILoadingBanner = () => (
  <div className="flex items-center gap-3 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl text-sm">
    <div className="flex gap-1 flex-shrink-0">
      <span className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
    <span className="text-purple-700 dark:text-purple-300 font-medium">
      A IA está gerando insights personalizados...
    </span>
  </div>
);

// ── Chat Message ─────────────────────────────────────────────────────────────
const ChatMessage = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser
          ? 'bg-primary-100 dark:bg-primary-900/30'
          : 'bg-purple-100 dark:bg-purple-900/30'
      }`}>
        {isUser
          ? <User size={16} className="text-primary-600" />
          : <Bot size={16} className="text-purple-600" />
        }
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
        isUser
          ? 'bg-primary-600 text-white'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
      }`}>
        {isUser ? (
          <p className="text-sm leading-relaxed">{message.content}</p>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            className="text-sm leading-relaxed"
            components={{
              h1: ({ children }) => (
                <h1 className="text-base font-bold text-gray-900 dark:text-white mt-3 mb-1 first:mt-0">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-sm font-bold text-gray-900 dark:text-white mt-3 mb-1 first:mt-0">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-2 mb-0.5 first:mt-0">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed mb-2 last:mb-0">{children}</p>
              ),
              ul: ({ children }) => (
                <ul className="text-sm text-gray-800 dark:text-gray-200 space-y-0.5 my-1.5 ml-3 list-disc">{children}</ul>
              ),
              ol: ({ children }) => (
                <ol className="text-sm text-gray-800 dark:text-gray-200 space-y-0.5 my-1.5 ml-3 list-decimal">{children}</ol>
              ),
              li: ({ children }) => (
                <li className="text-sm leading-relaxed">{children}</li>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>
              ),
              em: ({ children }) => (
                <em className="italic text-gray-700 dark:text-gray-300">{children}</em>
              ),
              code: ({ inline, children }) =>
                inline ? (
                  <code className="bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>
                ) : (
                  <pre className="bg-gray-200 dark:bg-gray-600 rounded-lg p-3 my-2 overflow-x-auto">
                    <code className="text-xs font-mono text-gray-900 dark:text-gray-100">{children}</code>
                  </pre>
                ),
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-purple-400 pl-3 my-2 text-gray-600 dark:text-gray-400 italic">{children}</blockquote>
              ),
              hr: () => <hr className="border-gray-300 dark:border-gray-500 my-2" />,
              a: ({ href, children }) => (
                <a href={href} className="text-purple-600 dark:text-purple-400 underline" target="_blank" rel="noopener noreferrer">{children}</a>
              ),
            }}
          >
            {message.content}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const Recommendations = () => {
  const { recommendations, isLoading, isLoadingAI, refetch } = useRecommendations();
  const { messages, sendMessage, clearChat, isLoading: isChatLoading } = useAdvisorChat();
  const [isRefetching, setIsRefetching] = useState(false);
  const [activeTab, setActiveTab] = useState('insights');
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleRefresh = async () => {
    setIsRefetching(true);
    await refetch();
    setIsRefetching(false);
  };

  const handleSend = () => {
    const text = chatInput.trim();
    if (!text || isChatLoading) return;
    setChatInput('');
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (q) => {
    setChatInput('');
    sendMessage(q);
  };

  // Group by priority
  const highPriority = recommendations.filter((r) => r.priority === 'high');
  const mediumPriority = recommendations.filter((r) => r.priority === 'medium');
  const lowPriority = recommendations.filter((r) => r.priority === 'low' || !r.priority);

  return (
    <div className="space-y-6 pb-8">
      {/* AI-themed header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900/80 to-slate-900 text-white shadow-2xl p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-purple-500/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-blue-400/10 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center">
              <Sparkles size={28} className="text-purple-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Consultor Financeiro IA</h1>
              <p className="text-white/60 text-sm mt-0.5">
                Analise personalizada e chat com inteligencia artificial
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tab toggle */}
            <div className="flex bg-white/10 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('insights')}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeTab === 'insights'
                    ? 'bg-white/20 text-white'
                    : 'text-white/60 hover:text-white/80'
                )}
              >
                <LayoutList size={15} />
                Insights
              </button>
              <button
                onClick={() => setActiveTab('chat')}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeTab === 'chat'
                    ? 'bg-white/20 text-white'
                    : 'text-white/60 hover:text-white/80'
                )}
              >
                <MessageCircle size={15} />
                Chat
              </button>
            </div>
          </div>
        </div>
      </div>

      <PageTutorial
        pageId="advisor"
        title="Consultor IA"
        steps={[
          'Os insights sao gerados com base nos seus dados financeiros reais',
          'Use o chat para tirar duvidas especificas sobre suas financas',
          'As recomendacoes sao atualizadas conforme seus dados mudam',
        ]}
      />

      {/* ── INSIGHTS TAB ── */}
      {activeTab === 'insights' && (
        <>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<RefreshCw size={14} className={isRefetching ? 'animate-spin' : ''} />}
              onClick={handleRefresh}
              isLoading={isRefetching}
            >
              Atualizar Analise
            </Button>
          </div>

          {isLoading ? (
            <AdvisorLoadingScreen />
          ) : recommendations.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 size={32} />}
              title="Suas financas estao saudaveis!"
              description="Nenhuma recomendacao no momento. Continue com seus bons habitos financeiros!"
              action={handleRefresh}
              actionLabel="Verificar Novamente"
            />
          ) : (
            <div className="space-y-8">
              {/* Banner de IA carregando (aparece quando regras já estão visíveis) */}
              {isLoadingAI && <AILoadingBanner />}

              {/* Stats bar */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Alta Prioridade', count: highPriority.length, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20' },
                  { label: 'Media Prioridade', count: mediumPriority.length, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20' },
                  { label: 'Baixa Prioridade', count: lowPriority.length, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
                ].map((stat) => (
                  <div key={stat.label} className={`${stat.bg} rounded-2xl p-4 text-center border border-transparent`}>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.count}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {highPriority.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    🔴 Alta Prioridade
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">— Requer atencao imediata</span>
                  </h2>
                  <div className="space-y-3">
                    {highPriority.map((rec, idx) => (
                      <div
                        key={rec.id || idx}
                        className="animate-[fadeSlideIn_0.4s_ease-out_both]"
                        style={{ animationDelay: `${idx * 80}ms` }}
                      >
                        <RecommendationCard rec={rec} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {mediumPriority.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    🟡 Media Prioridade
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">— Importante a medio prazo</span>
                  </h2>
                  <div className="space-y-3">
                    {mediumPriority.map((rec, idx) => (
                      <div
                        key={rec.id || idx}
                        className="animate-[fadeSlideIn_0.4s_ease-out_both]"
                        style={{ animationDelay: `${(highPriority.length + idx) * 80}ms` }}
                      >
                        <RecommendationCard rec={rec} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {lowPriority.length > 0 && (
                <div>
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    🟢 Baixa Prioridade
                    <span className="text-xs font-normal text-gray-500 dark:text-gray-400">— Dicas e oportunidades</span>
                  </h2>
                  <div className="space-y-3">
                    {lowPriority.map((rec, idx) => (
                      <div
                        key={rec.id || idx}
                        className="animate-[fadeSlideIn_0.4s_ease-out_both]"
                        style={{ animationDelay: `${(highPriority.length + mediumPriority.length + idx) * 80}ms` }}
                      >
                        <RecommendationCard rec={rec} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── CHAT TAB ── */}
      {activeTab === 'chat' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
          {/* Chat header */}
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Bot size={16} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white">Consultor IA</p>
                <p className="text-xs text-emerald-500">Online</p>
              </div>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="xs"
                leftIcon={<Trash2 size={13} />}
                onClick={clearChat}
              >
                Limpar
              </Button>
            )}
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-16 h-16 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-4">
                  <Sparkles size={28} className="text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Ola! Sou seu consultor financeiro.
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6">
                  Tenho acesso as suas contas, transacoes, metas e dividas.
                  Pergunte qualquer coisa sobre suas financas!
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                  {SUGGESTED_QUESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestedQuestion(q)}
                      className="text-left text-sm px-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((msg, i) => (
                  <ChatMessage key={i} message={msg} />
                ))}
                {isChatLoading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                      <Bot size={16} className="text-purple-600" />
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl px-4 py-3">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </>
            )}
          </div>

          {/* Input area */}
          <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex gap-2">
              <textarea
                ref={inputRef}
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pergunte sobre suas financas..."
                rows={1}
                className="flex-1 resize-none rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white px-4 py-2.5 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button
                onClick={handleSend}
                disabled={!chatInput.trim() || isChatLoading}
                className="w-10 h-10 rounded-xl bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 flex items-center justify-center transition-colors"
              >
                <Send size={18} className="text-white" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">
              A IA analisa seus dados financeiros reais para dar conselhos personalizados
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recommendations;
